/** ***********
*
* <utils.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import moment from 'moment';
import { Observable } from 'rxjs';

export function randomString(length: number) {
    const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~';
    let result = '';

    while (length > 0) {
        const bytes = new Uint8Array(16);
        const random = window.crypto.getRandomValues(bytes);

        random.forEach(function (c) {
            if (length === 0) {
                return;
            }
            if (c < charset.length) {
                result += charset[c];
                length--;
            }
        });
    }
    return result;
}

/**
 * Transform a simple csv table into json key value object
 */
export function csvToJson(csv: string): Array<{ [key: string]: string }> {
    const rows = csv.trim().split('\n');
    const [headers] = rows.splice(0, 1);
    const keys = headers.split(';').map((header) => header.trim());

    return rows.map((row) => {
        const values = row.split(';').map((value) => value.trim());
        const jsonRow = {};
        for (let i = 0; i < keys.length; i++) {
            jsonRow[keys[i]] = values[i];
        }
        return jsonRow;
    });
}

/**
 * Transform french (JJ/MM/AAAA HH:MM) date into a valid moment date MM/JJ/AAAA HH:MM
 * @param date JJ/MM/AAAA HH:MM
 */
export function formatDate(date: string): string {
    const [JJ, MM, AAAA] = date.split('/');
    return moment([MM, JJ, AAAA].join('/')).toString();
}

/**
 * Transform an array to map key value
 */
export const transformArrayIntoMap = <T>(array: T[], keyLine: string): { [key: string]: T } => {
    const res = {};
    for (const line of array) {
        if (line) {
            res[`${line[keyLine]}`] = line;
        }
    }
    return res;
};

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> =
    Pick<T, Exclude<keyof T, Keys>>
    & {
        [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>
    }[Keys];

export type ItselfOrArray<T> = T|T[];

/**
 * @link https://stackoverflow.com/q/77047823
 */
export type AwaitedObservable<T> = T extends Observable<infer R> ? AwaitedObservable<R> : T;
